import { Delete } from "@mui/icons-material";
import { Box, Card, CardContent, Chip, CircularProgress, IconButton, Skeleton, Switch, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { gateway } from "../gateway";
import { resetCreatePlanState } from "../redux/features/createPlanStateSlice";
import { createPromoCodesState } from "../redux/features/promoCodesStateSlice";
import { socket } from "../sockets";
import { getPlanById } from "../utils";
import { BaseDialog } from "./common/BaseDialog";
import { EditPromoCode } from "./createPromoDiscount";
import { EditPlan } from "./editPlan";
import { LoaderComponent } from "./Loader";

const styles = {
  titleSx: {
    fontWeight: "bold",
    textAlign: "center",
  },
  contentSx: { textAlign: "center", color: "var(--fx-surface-text, #0F172A)" },
  actionSx: {
    justifyContent: "center",
  },
  btnCloseSx: {
    backgroundColor: "grey",
    color: "white",
    "&:hover": { backgroundColor: "var(--fx-surface-sidebar, #233044)" },
    margin: "0 8px",
  },
  btnAcceptSx: {
    backgroundColor: "#3c7cde",
    color: "white",
    "&:hover": { backgroundColor: "var(--fx-surface-sidebar, #233044)" },
    margin: "0 8px",
  },
};

export const ManagePlanList = () => {
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPrice, setIsLoadingPrice] = useState(false);
  const [isLoadingCodes, setIsLoadingCodes] = useState(false);
  const [openConfirmDeletePromo, setOpenConfirmDeletePromo] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState();
  const [selectedPromo, setSelectedPromo] = useState();
  const [openEditPlan, setOpenEditPlan] = useState(false);
  const [openEditPromo, setOpenEditPromo] = useState(false);
  const [openChangePlanStatus, setOpenChangePlanStatus] = useState(false);
  const dispatch = useDispatch();

  const token = sessionStorage.getItem("token");

  let plansWithPromoCode = [];

  const getAllPlans = async () => {
    try {
      setIsLoading(true);
      const plansResponse = await gateway.get("/paypal/plans?promoCodeDiscount=true", {
        headers: {
          Authorization: `${token}`,
        },
      });
      const basePlans = plansResponse?.data?.data.filter((item) => item.promoCodeDiscount === null).sort((a, b) => a.order - b.order);
      setPlans(basePlans);
      setIsLoading(false);
      setIsLoadingPrice(true);
      const promoCodePlans = plansResponse?.data?.data.filter((item) => item.promoCodeDiscount !== null);
      groupByPromoCode(basePlans, promoCodePlans);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const groupByPromoCode = (basePlans, promoCodePlans) => {
    let mergedPlans = basePlans.map((item) => {
      const matchingPlans = promoCodePlans.filter((planCode) => planCode.product_id === item.product_id).map((planCode) => ({ planId: planCode.paypalPlanId, promoCode: planCode.promoCodeDiscount }));
      const planWithPromo = {
        ...item,
        codes: matchingPlans,
      };
      if (planWithPromo.codes.length > 0) {
        plansWithPromoCode.push(planWithPromo);
      }

      return {
        ...item,
        codes: matchingPlans,
      };
    });
    setPlans(mergedPlans);
    getPlanPrices(mergedPlans);
    dispatch(createPromoCodesState({ planPromoDiscount: plansWithPromoCode }));
  };

  const getPlanPrices = async (plans) => {
    const promises = plans.map((plan) => {
      const { isFreeTrial, isCustom } = plan;
      const planId = isFreeTrial || isCustom ? plan?.id : plan?.paypalPlanId;
      return getPlanById(planId, isFreeTrial, isCustom);
    });
    const planDetails = (await Promise.allSettled(promises)).filter((result) => result.status === "fulfilled").map((result) => result.value);
    const mergedPlans = plans.map((item) => {
      const planData = planDetails.find((plan) => plan.id === item.id);
      return {
        ...item,
        price: planData.billing_cycles ? planData.billing_cycles.find((cycle) => cycle.tenure_type === "REGULAR")?.pricing_scheme?.fixed_price.value || "" : "",
      };
    });
    setPlans(mergedPlans);
    setIsLoadingPrice(false);
  };

  const deletePromoDiscount = async () => {
    setOpen(true);
    setIsLoadingCodes(true);
    const response = await gateway.delete(`/paypal/plans/${selectedPromo.planId}`, {
      headers: {
        Authorization: `${token}`,
      },
    });
    setOpen(false);
    if (response.status === 204) {
      removeCodeDiscount();
    }
    setOpenConfirmDeletePromo(false);
  };

  const changePlanStatus = async () => {
    setOpen(true);
    if (selectedPlan.status === "ACTIVE") {
      await gateway.delete(`/paypal/plans/${selectedPlan.paypalPlanId}`, {
        headers: {
          Authorization: `${token}`,
        },
      });
    } else {
    }

    setOpen(false);
    setOpenChangePlanStatus(false);
    getAllPlans();
  };

  const removeCodeDiscount = () => {
    setPlans((prev) => {
      return prev.map((item) => {
        if (item.id === selectedPlan.id) {
          return {
            ...item,
            codes: item.codes.filter((code) => code.planId !== selectedPromo.planId),
          };
        }
        return item;
      });
    });
    setIsLoadingCodes(false);
  };

  const closeOpenEditPromoCode = () => {
    setOpenEditPromo(false);
    dispatch(resetCreatePlanState());
  };

  const closeOpenEditPlan = () => {
    setOpenEditPlan(false);
    dispatch(resetCreatePlanState());
  };

  useEffect(() => {
    getAllPlans();
    socket.on("paypalCredentialsEvent", () => getAllPlans());
  }, []);

  useEffect(() => { });

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        marginTop: 0,
        p: 0,
        alignItems: "flex-start",
        position: "relative",
        overflow: "auto",
      }}
    >
      {isLoading ? (
        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}>
          <CircularProgress size={120}></CircularProgress>
        </Box>
      ) : (
        plans.map((plan, index) => (
          <Card
            key={index}
            variant="outlined"
            sx={{ margin: 5, textAlign: "start" }}
            onClick={(e) => {
              setSelectedPlan(plan);
              setOpenEditPlan(true);
            }}
          >
            <CardContent sx={{ display: "flex", justifyContent: "space-between" }}>
              <Box>
                <Typography fontSize={18} fontWeight={"bold"} component="div">
                  {plan.product.name}
                </Typography>

                {isLoadingCodes ? (
                  <Skeleton variant="rectangular" width={80} height={30}></Skeleton>
                ) : (
                  <>
                    <Typography sx={{ fontSize: 14, mt: 2, mb: 1 }}>Promo Codes</Typography>
                    <Box sx={{ display: "flex" }}>
                      {plan?.codes?.map((item, index) => (
                        <Box style={{ display: "flex", alignItems: "center" }}>
                          <Chip
                            label={item.promoCode}
                            variant="outlined"
                            color="primary"
                            sx={{ pr: 1 }}
                            clickable
                            deleteIcon={
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            }
                            onDelete={() => {
                              setSelectedPlan(plan);
                              setSelectedPromo(item);
                              setOpenConfirmDeletePromo(true);
                            }}
                            onClick={(e) => {
                              setSelectedPromo(item);
                              e.stopPropagation();
                              setOpenEditPromo(true);
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
              </Box>
              <Box sx={{ textAlign: "right" }}>
                {isLoadingPrice ? <Skeleton variant="rectangular" width={80} height={30}></Skeleton> : <Typography>{plan.price ? `$${plan.price}` : "N/A"}</Typography>}
                {plan.product.benefits.map((item) => (isLoadingPrice ? <Skeleton variant="rectangular" width={80} height={30} /> : <Typography fontSize={12}>{item.name}</Typography>))}
                <Switch
                  label="Active"
                  labelPlacement="start"
                  checked={plan.status === "ACTIVE"}
                  onClick={(e) => {
                    setSelectedPlan(plan);
                    e.stopPropagation();
                    setOpenChangePlanStatus(true);
                  }}
                ></Switch>
              </Box>
            </CardContent>
          </Card>
        ))
      )}
      <BaseDialog
        openState={openConfirmDeletePromo}
        setOpenState={setOpenConfirmDeletePromo}
        title="Delete promo discount"
        titleSx={styles.titleSx}
        content={`Are you sure you want to delete the promo code ${selectedPromo?.promoCode} of ${selectedPlan?.name} plan`}
        actionSx={styles.actionSx}
        actions={[
          {
            label: "Cancel",
            onClick: () => {
              setOpenConfirmDeletePromo(false);
            },
            style: styles.btnCloseSx,
          },
          {
            label: "Delete",
            onClick: () => {
              deletePromoDiscount();
            },
            style: styles.btnCloseSx,
          },
        ]}
      ></BaseDialog>
      <BaseDialog
        openState={openEditPlan}
        setOpenState={closeOpenEditPlan}
        content={<EditPlan id={selectedPlan?.paypalPlanId ? selectedPlan?.paypalPlanId : selectedPlan?.id} isFreePlan={selectedPlan?.paypalPlanId ? false : true} setDialogState={closeOpenEditPlan} resetDetails={getAllPlans}></EditPlan>}
      ></BaseDialog>
      <BaseDialog
        openState={openEditPromo}
        setOpenState={closeOpenEditPromoCode}
        content={<EditPromoCode id={selectedPromo?.planId} promoCode={selectedPromo?.promoCode} setOpenDialog={closeOpenEditPromoCode} resetDetails={getAllPlans}></EditPromoCode>}
      ></BaseDialog>

      <BaseDialog
        openState={openChangePlanStatus}
        setOpenState={setOpenChangePlanStatus}
        titleSx={styles.titleSx}
        title="Confirm Plan Desactivation"
        contentSx={styles.contentSx}
        content={`Are you sure you want to ${selectedPlan?.status === "ACTIVE" ? "deactivate" : "activate"} the ${selectedPlan?.name} plan?`}
        actionSx={styles.actionSx}
        actions={[
          {
            label: "Cancel",
            onClick: () => {
              setOpenChangePlanStatus(false);
            },
            style: styles.btnCloseSx,
          },
          {
            label: selectedPlan?.status === "ACTIVE" ? "DEACTIVATE PLAN" : "ACTIVATE PLAN",
            onClick: () => {
              changePlanStatus();
            },
            style: styles.btnCloseSx,
          },
        ]}
      ></BaseDialog>
      <LoaderComponent open={open} type="spinner"></LoaderComponent>
    </Box>
  );
};
